import { useEffect, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Lock } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/design-system/components/ui";
import { InternalUserPicker } from "@/components/users/InternalUserPicker";
import { foldersApi } from "@/api/folders";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/api/client";

type Mode = "everyone" | "admin" | "users";

interface FolderPermissionsModalProps {
  open: boolean;
  onClose: () => void;
  folderId: string | null;
  folderName?: string;
  /** Fired after a successful save so the host can refetch the tree. */
  onSaved?: () => void;
}

/**
 * Folder Visibility &amp; Access Control v1.0 — ADMIN-only modal to restrict
 * who can see a folder (and, by cascade, every subfolder and document under
 * it). Three states map to the backend's Everyone / Restricted model:
 *   • Everyone — no restriction.
 *   • Admin only — Restricted with zero grants.
 *   • Specific users — Restricted with an explicit grant list (admins always
 *     retain access on top of the listed users).
 */
export function FolderPermissionsModal({
  open,
  onClose,
  folderId,
  folderName,
  onSaved,
}: FolderPermissionsModalProps) {
  const { toast } = useToast();
  const [mode, setMode] = useState<Mode>("everyone");
  const [userIds, setUserIds] = useState<string[]>([]);

  const access = useQuery({
    queryKey: ["dms", "folderAccess", folderId],
    queryFn: () => foldersApi.getAccess(folderId!),
    enabled: open && !!folderId,
  });

  // Seed local state once the current access state loads.
  useEffect(() => {
    const data = access.data;
    if (!data) return;
    if (!data.isRestricted) {
      setMode("everyone");
      setUserIds([]);
    } else if (data.userIds.length > 0) {
      setMode("users");
      setUserIds(data.userIds);
    } else {
      setMode("admin");
      setUserIds([]);
    }
  }, [access.data]);

  const save = useMutation({
    mutationFn: () =>
      foldersApi.setAccess(folderId!, {
        restricted: mode !== "everyone",
        userIds: mode === "users" ? userIds : [],
      }),
    onSuccess: (result) => {
      onSaved?.();
      toast({
        message: result.isRestricted
          ? "Folder access restricted."
          : "Folder is now visible to everyone.",
        tone: "success",
      });
      onClose();
    },
    onError: (err) => {
      const msg =
        err instanceof ApiError
          ? err.errors[0] ?? err.message
          : "Could not update folder access.";
      toast({ message: msg, tone: "error" });
    },
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Folder permissions"
      description={folderName ? `Who can access “${folderName}”` : undefined}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => save.mutate()}
            disabled={save.isPending || access.isLoading}
          >
            {save.isPending ? "Saving…" : "Save"}
          </Button>
        </>
      }
    >
      {access.isLoading ? (
        <p className="text-small text-gray-mid">Loading current access…</p>
      ) : (
        <div className="flex flex-col gap-space-4">
          <p className="text-caption text-gray-mid flex items-start gap-space-2">
            <Lock size={14} className="shrink-0 mt-[2px] text-gold" aria-hidden />
            Restricting a folder hides it — and every subfolder and document
            inside it — from anyone who isn’t granted access. Administrators
            always retain access.
          </p>

          <fieldset className="flex flex-col gap-space-3 border-0 p-0 m-0">
            <legend className="sr-only">Folder access</legend>

            <label className="flex items-start gap-space-2 text-small text-onyx cursor-pointer">
              <input
                type="radio"
                name="folder-access-mode"
                className="mt-[3px]"
                checked={mode === "everyone"}
                onChange={() => setMode("everyone")}
              />
              <span>
                <span className="font-semibold">Everyone</span>
                <span className="block text-caption text-gray-mid">
                  Any staff member with document access can see this folder.
                </span>
              </span>
            </label>

            <label className="flex items-start gap-space-2 text-small text-onyx cursor-pointer">
              <input
                type="radio"
                name="folder-access-mode"
                className="mt-[3px]"
                checked={mode === "admin"}
                onChange={() => setMode("admin")}
              />
              <span>
                <span className="font-semibold">Admin only</span>
                <span className="block text-caption text-gray-mid">
                  Only administrators can see this folder.
                </span>
              </span>
            </label>

            <label className="flex items-start gap-space-2 text-small text-onyx cursor-pointer">
              <input
                type="radio"
                name="folder-access-mode"
                className="mt-[3px]"
                checked={mode === "users"}
                onChange={() => setMode("users")}
              />
              <span>
                <span className="font-semibold">Specific users</span>
                <span className="block text-caption text-gray-mid">
                  Only the selected users (plus administrators) can see it.
                </span>
              </span>
            </label>
          </fieldset>

          {mode === "users" && (
            <div className="flex flex-col gap-space-2">
              <span className="text-caption uppercase tracking-wide text-gray-mid">
                Granted users
              </span>
              <InternalUserPicker value={userIds} onChange={setUserIds} />
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
